import React, { useState, useEffect } from 'react';
import Header from '../Components/Header';
import Footer from '../Components/Footer';
import { Link } from 'react-router-dom';
import {
  Mail, Phone, Building, FileText, MessageSquare, CheckCircle, X, UserCircle
} from 'lucide-react';
import SimpleKakaoButton from '../Components/SimpleKakaoButton';
import TagManager from 'react-gtm-module';

const ContactForm = () => {
  // 폼 데이터 상태
  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    contactMethod: 'email',
    contact: '', // 이메일 또는 전화번호
    inquiry: '',
    privacy: false
  });

  // 로딩 및 성공 메시지 상태
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // 폼 입력 핸들러
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      // 연락 방법이 변경되면 contact 필드 초기화
      ...(name === 'contactMethod' ? { contact: '' } : {})
    }));
  };

  // 폼 제출 핸들러
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 유효성 검사
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = '이름을 입력해주세요.';
    if (!formData.companyName.trim()) newErrors.companyName = '회사명을 입력해주세요.';
    if (!formData.contact.trim()) newErrors.contact = formData.contactMethod === 'email' ? '이메일을 입력해주세요.' : '전화번호를 입력해주세요.';
    if (!formData.inquiry.trim()) newErrors.inquiry = '문의 내용을 입력해주세요.';
    if (!formData.privacy) newErrors.privacy = '개인정보처리방침에 동의해주세요.';
    
    // 이메일 형식 검사
    if (formData.contactMethod === 'email' && formData.contact && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact)) {
      newErrors.contact = '유효한 이메일 주소를 입력해주세요.';
    }
    
    // 전화번호 형식 검사
    if (formData.contactMethod !== 'email' && formData.contact && !/^[\d\-\+\(\)]{8,20}$/.test(formData.contact)) {
      newErrors.contact = '유효한 전화번호를 입력해주세요.';
    }

    if (Object.keys(newErrors).length > 0) {
      console.log('Validation errors:', newErrors);
      return;
    }

    setIsLoading(true);
    try {
      // Google Sheets API 엔드포인트
      const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzln4YMAbLsLxbpSvlEgN9D3er1am0YosIubXqnTV5UCzN4PWLYpg3CPO0MzQgB3kHS/exec';
      
      // 데이터 구성
      const submitData = {
        companyName: formData.companyName,
        name: formData.name,
        position: '',
        email: formData.contactMethod === 'email' ? formData.contact : '',
        phone: formData.contactMethod !== 'email' ? formData.contact : '',
        companySize: '',
        industry: '',
        preferredContact: formData.contactMethod,
        preferredTime: '',
        inquiry: formData.inquiry,
        howDidYouHear: 'contact_page',
        formType: 'contact_inquiry'
      };

      console.log('Submitting data:', submitData);

      await fetch(SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        redirect: 'follow',
        body: JSON.stringify(submitData)
      });

      // 성공 처리
      setFormSubmitted(true);
      setSuccessMessage({
        title: '문의가 성공적으로 접수되었습니다!',
        message: '24시간 내에 담당자가 연락드릴 예정입니다.'
      });
      
      // GTM 이벤트 전송
      window.dataLayer = window.dataLayer || [];
      TagManager.dataLayer({
        dataLayer: {
          event: 'form_submission_success',
          form_name: 'contact_form',
          user_email: formData.contactMethod === 'email' ? formData.contact : '',
          contact_method: formData.contactMethod,
          form_type: 'contact_inquiry'
        }
      });

      // 폼 초기화
      setFormData({
        companyName: '',
        name: '',
        contactMethod: 'email',
        contact: '',
        inquiry: '',
        privacy: false
      });
      
    } catch (error) {
      console.error('Submission error:', error);
      setSuccessMessage({
        title: '문의 접수 중 오류가 발생했습니다.',
        message: '잠시 후 다시 시도해주시거나 카카오톡으로 문의해주세요.',
        isError: true
      });
    } finally {
      setIsLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // 성공 메시지 닫기
  const closeSuccessMessage = () => {
    setSuccessMessage(null);
    setFormSubmitted(false);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* 성공 메시지 모달 */}
      {successMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full animate-fade-in">
            <div className={`p-4 ${successMessage.isError ? 'bg-red-500' : 'bg-green-500'} text-white flex justify-between items-center rounded-t-xl`}>
              <h3 className="text-lg font-bold">{successMessage.isError ? '오류 발생' : '성공!'}</h3>
              <button onClick={closeSuccessMessage} className="text-white hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 text-center">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${successMessage.isError ? 'bg-red-100 text-red-500' : 'bg-green-100 text-green-500'} mb-4`}>
                {successMessage.isError ? (
                  <X className="w-8 h-8" />
                ) : (
                  <CheckCircle className="w-8 h-8" />
                )}
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">{successMessage.title}</h2>
              <p className="text-gray-600 mb-6">{successMessage.message}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={closeSuccessMessage}
                  className={`px-6 py-3 rounded-lg shadow-md transition duration-200 ${
                    successMessage.isError 
                      ? 'bg-red-600 text-white hover:bg-red-700' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {successMessage.isError ? '다시 시도하기' : '추가 문의하기'}
                </button>
                {!successMessage.isError && (
                  <Link to="/">
                    <button className="px-6 py-3 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition duration-200">
                      홈페이지로 돌아가기
                    </button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <Header />

      {/* 히어로 섹션 */}
      <section className="bg-gradient-to-r from-blue-700 to-indigo-800 text-white py-20 mt-16 relative overflow-hidden">
        {/* 배경 패턴 */}
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* 배경 원형 장식 */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl opacity-20 transform translate-x-1/3 -translate-y-1/3"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-600 rounded-full filter blur-3xl opacity-20 transform -translate-x-1/3 translate-y-1/3"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <div className="inline-block px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium mb-4">
              SWITCHUP 서비스 문의
            </div>
            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-4">
              비즈니스 성장을 함께 <br className="hidden sm:block" />만들어보세요
            </h1>
            <p className="text-lg text-blue-100 mb-6">
              SWITCHUP의 전환율 최적화 서비스로 데이터 기반 의사결정을 시작하세요.<br className="hidden md:block" />
              아래 양식을 작성하시면 맞춤형 제안과 함께 빠르게 답변 드리겠습니다.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/pricing">
                <button className="px-6 py-3 bg-white text-blue-700 rounded-lg shadow-lg hover:bg-blue-50 transition duration-200 font-medium">
                  가격 확인하기
                </button>
              </Link>
              <Link to="/service/ab-test">
                <button className="px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-lg hover:bg-white/20 transition duration-200 font-medium">
                  서비스 살펴보기
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 메인 폼 섹션 */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {!formSubmitted && (
            <div className="bg-white rounded-2xl shadow-xl border border-blue-100 overflow-hidden">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 px-8 py-8">
                <div className="text-center">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    서비스 문의
                  </h2>
                  <p className="text-gray-600">
                    간단한 정보만 남겨주시면 24시간 내에 연락드리겠습니다
                  </p>
                </div>
              </div>

              <div className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* 회사명 */}
                    <div>
                      <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                        회사명 <span className="text-red-500">*</span>
                      </label>
                      <div className="flex items-center border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-colors bg-white">
                        <div className="pl-3">
                          <Building className="w-5 h-5 text-blue-500" />
                        </div>
                        <input
                          type="text"
                          id="companyName"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleFormChange}
                          placeholder="회사명을 입력해주세요"
                          className="w-full px-3 py-3 outline-none bg-transparent"
                          required
                        />
                      </div>
                    </div>

                    {/* 이름 */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        이름 <span className="text-red-500">*</span>
                      </label>
                      <div className="flex items-center border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-colors bg-white">
                        <div className="pl-3">
                          <UserCircle className="w-5 h-5 text-blue-500" />
                        </div>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleFormChange}
                          placeholder="이름을 입력해주세요"
                          className="w-full px-3 py-3 outline-none bg-transparent"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* 선호하는 연락 방법 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      선호하는 연락 방법 <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { value: 'email', label: '이메일', icon: <Mail className="w-4 h-4" /> },
                        { value: 'phone', label: '전화', icon: <Phone className="w-4 h-4" /> },
                        { value: 'sms', label: '문자', icon: <MessageSquare className="w-4 h-4" /> },
                        { value: 'kakao', label: '카카오톡', icon: <MessageSquare className="w-4 h-4" /> }
                      ].map((method) => (
                        <div key={method.value}>
                          <input
                            type="radio"
                            id={`contact-${method.value}`}
                            name="contactMethod"
                            value={method.value}
                            checked={formData.contactMethod === method.value}
                            onChange={handleFormChange}
                            className="sr-only"
                          />
                          <label
                            htmlFor={`contact-${method.value}`}
                            className={`flex items-center justify-center px-4 py-3 border rounded-lg cursor-pointer transition-all duration-200 ${
                              formData.contactMethod === method.value
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-300 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                            }`}
                          >
                            {method.icon}
                            <span className="ml-2 text-sm font-medium">{method.label}</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 연락처 (이메일 또는 전화번호) */}
                  <div>
                    {formData.contactMethod === 'email' ? (
                      <>
                        <label htmlFor="contact" className="block text-sm font-medium text-gray-700 mb-2">
                          이메일 <span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-colors bg-white">
                          <div className="pl-3">
                            <Mail className="w-5 h-5 text-blue-500" />
                          </div>
                          <input
                            type="email"
                            id="contact"
                            name="contact"
                            value={formData.contact}
                            onChange={handleFormChange}
                            placeholder="이메일을 입력해주세요"
                            className="w-full px-3 py-3 outline-none bg-transparent"
                            required
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <label htmlFor="contact" className="block text-sm font-medium text-gray-700 mb-2">
                          전화번호 <span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-colors bg-white">
                          <div className="pl-3">
                            <Phone className="w-5 h-5 text-blue-500" />
                          </div>
                          <input
                            type="tel"
                            id="contact"
                            name="contact"
                            value={formData.contact}
                            onChange={handleFormChange}
                            placeholder="010-1234-5678"
                            className="w-full px-3 py-3 outline-none bg-transparent"
                            required
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* 문의 내용 */}
                  <div>
                    <label htmlFor="inquiry" className="block text-sm font-medium text-gray-700 mb-2">
                      문의 내용 <span className="text-red-500">*</span>
                    </label>
                    <div className="flex border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-colors bg-white">
                      <div className="pl-3 pt-3">
                        <FileText className="w-5 h-5 text-blue-500" />
                      </div>
                      <textarea
                        id="inquiry"
                        name="inquiry"
                        value={formData.inquiry}
                        onChange={handleFormChange}
                        rows="4"
                        placeholder="어떤 점이 궁금하신가요? 프로젝트 예산이나 일정 등 구체적인 내용을 함께 작성해주시면 더 정확한 답변이 가능합니다."
                        className="w-full px-3 py-3 outline-none bg-transparent resize-none"
                        required
                      />
                    </div>
                    <div className="mt-2 text-right text-sm text-gray-500">
                      {formData.inquiry.length}/1000자
                    </div>
                  </div>

                  {/* 개인정보 동의 */}
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="privacy"
                      name="privacy"
                      checked={formData.privacy}
                      onChange={handleFormChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-1"
                      required
                    />
                    <label htmlFor="privacy" className="ml-3 text-sm text-gray-600">
                      <Link to="/privacy" className="text-blue-600 hover:underline">개인정보처리방침</Link>에 동의합니다. <span className="text-red-500">*</span>
                    </label>
                  </div>

                  {/* 제출 버튼 */}
                  <div className="text-center pt-4">
                    <button
                      type="submit"
                      disabled={!formData.name || !formData.companyName || !formData.contact || !formData.inquiry || !formData.privacy || isLoading}
                      className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          처리중...
                        </div>
                      ) : (
                        '문의하기'
                      )}
                    </button>
                    <p className="text-sm text-gray-500 mt-3">
                      문의 후 24시간 내에 담당자가 연락드립니다
                    </p>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* 추가 정보 섹션 */}
          <div className="mt-12 grid md:grid-cols-2 gap-8">
            {/* 도입 프로세스 */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-xl font-bold text-gray-800 mb-6">도입 프로세스</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                    1
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-medium text-gray-800">문의 접수</h4>
                    <p className="text-gray-600">양식 제출 시 즉시 담당자에게 전달됩니다.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                    2
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-medium text-gray-800">맞춤 제안</h4>
                    <p className="text-gray-600">24시간 이내에 귀사의 상황에 맞는 솔루션을 제안해 드립니다.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                    3
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-medium text-gray-800">서비스 도입</h4>
                    <p className="text-gray-600">계약 체결 후 빠르게 서비스를 시작할 수 있습니다.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 자주 묻는 질문 */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-xl font-bold text-gray-800 mb-6">자주 묻는 질문</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-medium text-gray-800 mb-1">서비스 도입에 개발 지식이 필요한가요?</h4>
                  <p className="text-gray-600">아니요, 단 한 줄의 코드만 추가하면 누구나 쉽게 시작할 수 있습니다.</p>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-gray-800 mb-1">무료 체험 기간이 있나요?</h4>
                  <p className="text-gray-600">네, 모든 고객에게 30일 무료 체험 기간을 제공합니다.</p>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-gray-800 mb-1">기술 지원은 어떻게 받을 수 있나요?</h4>
                  <p className="text-gray-600">이메일과 카카오톡을 통해 실시간 기술 지원을 제공합니다.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 카카오 문의 버튼 */}
      {/* <div className="fixed bottom-6 right-6 z-40">
        <SimpleKakaoButton
        channelId="_yixgxjn"
        buttonText="카카오로 문의하기"
        mobileText="카카오 문의"
      />
      </div> */}

      {/* 애니메이션 스타일 */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
        
        @media (prefers-reduced-motion) {
          .animate-fade-in {
            animation: none;
          }
        }
      `}</style>

      <Footer />
    </div>
  );
};

export default ContactForm;